/**
 * Service générique pour construire le contexte des formulaires de recherche
 * Centralise toute la logique spécifique aux routes, stores, et règles de visibilité
 */

/**
 * Construit le contexte additionnel basé sur la route et le store
 * @param {Object} route - Objet route Vue Router
 * @param {Object} store - Store Vuex
 * @returns {Object} Contexte additionnel
 */
export function buildSearchFormContext(route, store) {
  const context = {}
  
  // === UTILISATEUR SITIMMO ===
  if (store && store.getters.getSitimmoUser) {
    const user = store.getters.getSitimmoUser
    
    // Contexte utilisateur structuré
    context.sitimmoUser = {
      stocks: user.stocks ? user.stocks.length : 0
    }
    
    //  RÈGLES DE VISIBILITÉ : Clés plates pour le backend
    context['sitimmoUser.stocks'] = user.stocks ? user.stocks.length : 0
  }
  
  // === CONTEXTE SPÉCIFIQUE AUX ROUTES ===
  const routeContext = buildRouteSpecificContext(route, store)
  Object.assign(context, routeContext)
  
  return context
}

/**
 * Construit le contexte spécifique à une route
 * @param {Object} route - Objet route Vue Router
 * @param {Object} store - Store Vuex
 * @returns {Object} Contexte spécifique à la route
 */
function buildRouteSpecificContext(route, store) {
  const context = {}
  
  // === ROUTES AVEC ANNONCE PARENTE (lots, programmes, etc.) ===
  if (isRouteWithParentAnnouncement(route)) {
    // Données depuis le store
    if (store && store.getters['postsListImmo/annonceParente']) {
      context.annonceParente = store.getters['postsListImmo/annonceParente']
      context.has_annonce_parente = true
    }
    
    // Fallback depuis les paramètres de route
    if (route.params.programId) {
      context.programId = route.params.programId
      context.has_annonce_parente = true
    }
  }
  
  // === AUTRES ROUTES SPÉCIFIQUES ===
  // Ajouter ici d'autres logiques spécifiques aux routes
  // Exemple : routes CMI, routes promoteur, etc.
  
  return context
}

/**
 * Détermine si une route nécessite un contexte d'annonce parente
 * @param {Object} route - Objet route Vue Router
 * @returns {boolean}
 */
function isRouteWithParentAnnouncement(route) {
  const routesWithParent = [
    'posts-lots',
    'posts/lots',
    'content-library-lots'
  ]
  
  return routesWithParent.includes(route.name) || 
         route.path.includes('/lots') ||
         route.params.programId
}

/**
 * Construit le contexte pour les règles de visibilité
 * Centralise toutes les clés plates nécessaires au backend
 * @param {Object} store - Store Vuex
 * @param {Object} cachedData - Données déjà chargées par le composant (users, contacts, etc.)
 * @returns {Object} Contexte pour les règles de visibilité
 */
export function buildVisibilityRulesContext(store, cachedData = {}) {
  const context = {}
  
  if (store && store.getters.getSitimmoUser) {
    const user = store.getters.getSitimmoUser
    
    // Nombre de stocks pour règle de visibilité
    context['sitimmoUser.stocks'] = user.stocks ? user.stocks.length : 0
    
    // Nombre de stocks pour les contextStats du backend
    context['stocks_count'] = user.stocks ? user.stocks.length : 0
    
    // Utilisateurs & contacts déjà chargés par le composant
    if (cachedData.users && Array.isArray(cachedData.users)) {
      context['users_count'] = cachedData.users.length
    }
    
    if (cachedData.contacts && Array.isArray(cachedData.contacts)) {
      context['contacts_count'] = cachedData.contacts.length
    }
    
    // Type et rôles utilisateur pour règles de visibilité
    context['user_type'] = mapMetierToUserType(user.metier)
    context['user.roles'] = determineUserRoles(store)
    
    // Login utilisateur nécessaire pour filtrer les objectTypes dans le contexte des lots
    context['sitimmo_user_login'] = user.login || null
    
    // ID utilisateur et statut beta testeur pour règles de visibilité (ex: champ négociateur)
    context['sitimmoUser.id'] = user.id || null
    context['betaTesteur'] = user.betaTesteur || false
  }

  // === DATES CONTACT MANAGER ===
  // Récupérer les dates depuis le store global 'donnees' pour les pages Contact Manager
  if (store && store.getters['donnees/getContactsDateBefore'] !== undefined && store.getters['donnees/getContactsDateAfter'] !== undefined) {
    const beforeContacts = store.getters['donnees/getContactsDateBefore']
    const afterContacts = store.getters['donnees/getContactsDateAfter']
    
    // Convertir les objets moment en string ISO si nécessaire
    context['beforeContacts'] = beforeContacts && beforeContacts.format ? beforeContacts.format('YYYY-MM-DD') : beforeContacts
    context['afterContacts'] = afterContacts && afterContacts.format ? afterContacts.format('YYYY-MM-DD') : afterContacts
    
    console.log('📅 [contextBuilder] Dates CM envoyées au backend:', {
      beforeContacts: context['beforeContacts'],
      afterContacts: context['afterContacts']
    })
  }
  
  return context
}

/**
 * Mappe le métier Sitimmo vers le type d'utilisateur normalisé
 * @param {string} metier - Métier Sitimmo (AGENCE, PROMOTEUR, etc.)
 * @returns {string} Type d'utilisateur normalisé
 */
function mapMetierToUserType(metier) {
  const mapping = {
    'AGENCE': 'agence',
    'PROMOTEUR': 'promoteur', 
    'BAILLEUR_SOCIAL': 'bailleur_social',
    'CMI': 'cmi'
  }
  
  return mapping[metier] || 'agence'
}

/**
 * Détermine les rôles de l'utilisateur pour les règles de visibilité
 * @param {Object} store - Store Vuex
 * @returns {Array<string>} Tableau des rôles utilisateur
 */
function determineUserRoles(store) {
  const roles = []

  if (store.getters.isMainAccount) {
    roles.push('ROLE_MAIN_ACCOUNT')
  }

  if (store.getters.isSubAdmin) {
    roles.push('ROLE_SUB_ADMIN')
  }

  if (store.getters.isSubUser) {
    roles.push('ROLE_SUB_USER')
  }

  // Fallback si aucun rôle spécifique détecté
  if (roles.length === 0) {
    roles.push('ROLE_USER')
  }

  return roles
}

/**
 * Nettoie les filtres 'user', 'contact' et '_cachedUsers' si l'utilisateur est un ROLE_SUB_USER
 * Ceci évite qu'un sous-utilisateur ne voie les annonces de ses collègues
 * en raison de filtres persistants dans le store
 * @param {Object} store - Store Vuex
 */
function cleanForbiddenFiltersForSubUser(store) {
  if (store.getters.isSubUser) {
    // Déterminer le store module à nettoyer selon la route
    const storeName = determineStoreModule(store)
    const storeModule = store.state[storeName]
    const currentFilters = (storeModule && storeModule.filter) || {}

    // Vérifier si des filtres interdits sont présents
    if (currentFilters.user || currentFilters.contact || currentFilters._cachedUsers) {
      // Supprimer les filtres interdits
      const cleanedFilters = { ...currentFilters }
      delete cleanedFilters.user
      delete cleanedFilters.contact
      delete cleanedFilters._cachedUsers

      // Mettre à jour le store avec les filtres nettoyés
      const mutationName = `${storeName}/replaceFilter`
      if (store._mutations[mutationName]) {
        store.commit(mutationName, cleanedFilters)
      }
    }
  }
}

/**
 * Détermine le module store à utiliser selon le contexte
 * @param {Object} store - Store Vuex
 * @returns {string} Nom du module store
 */
function determineStoreModule(store) {
  const route = store.state.route || {}

  // Mapping route → store module
  if (route.name && route.name.includes('vo')) {
    return 'postsVo'
  }

  if (route.name && route.name.includes('broadcast')) {
    return 'broadcast'
  }
  
  // Par défaut : postsListImmo
  return 'postsListImmo'
}

/**
 * Point d'entrée principal - combine tous les contextes
 * @param {Object} route - Objet route Vue Router
 * @param {Object} store - Store Vuex
 * @param {Object} cachedData - Données déjà chargées par le composant (users, contacts, etc.)
 * @returns {Object} Contexte complet
 */
export function buildCompleteSearchFormContext(route, store, cachedData = {}) {
  // Nettoyer les filtres interdits pour les ROLE_SUB_USER
  cleanForbiddenFiltersForSubUser(store)

  const baseContext = buildSearchFormContext(route, store)
  const visibilityContext = buildVisibilityRulesContext(store, cachedData)
  
  return {
    ...baseContext,
    ...visibilityContext
  }
}
